import { supabase, allowOfflineFallback } from './mockData';
import { Pet } from '../types';

export async function getPets(): Promise<Pet[]> {
  try {
    const { data, error } = await supabase
      .from('pets')
      .select(`
        *,
        owner:owners(id, name, phone, email, address)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw new Error(error.message);
    
    // Supabase'den gelen veriyi Pet interface'ine uygun hale getir
    return (data || []).map((item: any) => ({
      id: item.id,
      name: item.name || '',
      species: item.species || 'dog',
      breed: item.breed || '',
      age: item.age || 0,
      weight: item.weight || 0,
      gender: item.gender || 'male',
      color: item.color || '',
      owner_id: item.owner_id || '',
      protocolNo: item.protocol_no || '',
      neutered: item.neutered || false,
      lastVisitDate: item.last_visit_date || '',
      owner: item.owner ? {
        id: item.owner.id,
        name: item.owner.name || '',
        phone: item.owner.phone || '',
        email: item.owner.email || '',
        address: item.owner.address || '',
        chipNumber: item.owner.chip_number || '',
        passportNumber: item.owner.passport_number || '',
        createdAt: item.owner.created_at || new Date().toISOString()
      } : undefined,
      createdAt: item.created_at || new Date().toISOString()
    })) as Pet[];
  } catch (error) {
    console.error('getPets:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const { mockPets, getPetWithOwner } = await import('./mockData');
    return mockPets.map(pet => {
      const petWithOwner = getPetWithOwner(pet.id);
      return petWithOwner || pet;
    });
  }
}

export async function addPet(pet: Omit<Pet, 'id' | 'createdAt'>): Promise<Pet> {
  try {
    const petData = {
      name: pet.name,
      species: pet.species,
      breed: pet.breed,
      age: pet.age,
      weight: pet.weight,
      gender: pet.gender,
      color: pet.color,
      owner_id: pet.owner_id,
      protocol_no: pet.protocolNo,
      neutered: pet.neutered,
      last_visit_date: pet.lastVisitDate
    };

    const { data, error } = await supabase.from('pets').insert([petData]).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...pet,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Pet;
  } catch (error) {
    console.error('pets insert:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const newPet: Pet = {
      ...pet,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    
    const existingPets = JSON.parse(localStorage.getItem('pets') || '[]');
    existingPets.push(newPet);
    localStorage.setItem('pets', JSON.stringify(existingPets));
    
    return newPet;
  }
}

export async function updatePet(id: string, pet: Partial<Pet>): Promise<Pet> {
  try {
    const updateData: any = {};
    
    if (pet.name !== undefined) updateData.name = pet.name;
    if (pet.species !== undefined) updateData.species = pet.species;
    if (pet.breed !== undefined) updateData.breed = pet.breed;
    if (pet.age !== undefined) updateData.age = pet.age;
    if (pet.weight !== undefined) updateData.weight = pet.weight;
    if (pet.gender !== undefined) updateData.gender = pet.gender;
    if (pet.color !== undefined) updateData.color = pet.color;
    if (pet.owner_id !== undefined) updateData.owner_id = pet.owner_id;
    if (pet.protocolNo !== undefined) updateData.protocol_no = pet.protocolNo;
    if (pet.neutered !== undefined) updateData.neutered = pet.neutered;
    if (pet.lastVisitDate !== undefined) updateData.last_visit_date = pet.lastVisitDate;

    const { data, error } = await supabase.from('pets').update(updateData).eq('id', id).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...pet,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Pet;
  } catch (error) {
    console.error('pets update:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingPets = JSON.parse(localStorage.getItem('pets') || '[]');
    const petIndex = existingPets.findIndex((p: Pet) => p.id === id);
    
    if (petIndex !== -1) {
      existingPets[petIndex] = { ...existingPets[petIndex], ...pet };
      localStorage.setItem('pets', JSON.stringify(existingPets));
      return existingPets[petIndex];
    }
    
    throw new Error('Pet bulunamadı');
  }
}

export async function deletePet(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('pets').delete().eq('id', id);
    if (error) throw new Error(error.message);
  } catch (error) {
    console.error('pets delete:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingPets = JSON.parse(localStorage.getItem('pets') || '[]');
    const filteredPets = existingPets.filter((p: Pet) => p.id !== id);
    localStorage.setItem('pets', JSON.stringify(filteredPets));
  }
}